import { animated, useSpring } from '@react-spring/web';
import { css } from 'aphrodite';
import * as React from 'react';
import { styleSheet } from './styles';

interface IProps {
	children: React.ReactNode;
	isCollapsed?: boolean;
}

/**
 * Reads the border box rather than `getBoundingClientRect`, which reports transform-scaled
 * values. These panels render inside React Flow's zoomed canvas viewport, where a scaled
 * measurement animates the panel to the wrong height.
 */
const useBorderBoxHeight = () => {
	const ref = React.useRef<HTMLDivElement>(null);
	const [height, setHeight] = React.useState<number | null>(null);

	React.useEffect(() => {
		const element = ref.current;
		if (!element) {
			return;
		}

		const observer = new ResizeObserver(entries => {
			for (const entry of entries) {
				const borderBox = entry.borderBoxSize?.[0];
				setHeight(borderBox ? borderBox.blockSize : entry.contentRect.height);
			}
		});
		observer.observe(element);

		return () => observer.disconnect();
	}, []);

	return [ref, height] as const;
};

export const Collapsible: React.FC<IProps> = ({ children, isCollapsed = true }) => {
	const [contentRef, height] = useBorderBoxHeight();
	const styleProps = useSpring({
		height: isCollapsed ? 0 : (height ?? 0),
	});

	// Once open and at rest the panel stops pinning a measured height, so content that grows
	// afterwards (a revealed sub-form, a late-loading font) cannot spill out of it. Before the
	// first measurement there is no height to settle at, so it stays closed rather than opening
	// to `auto` and snapping shut when the spring starts.
	const isSettledOpen = (h: number) => !isCollapsed && height !== null && h === height;

	return (
		<animated.div
			style={{
				height: styleProps.height.to(h => (isSettledOpen(h) ? 'auto' : h)),
				overflow: styleProps.height.to(h => (isSettledOpen(h) ? 'unset' : 'hidden')),
			}}
		>
			<div ref={contentRef} className={css(styleSheet.collapsibleInner)}>
				{children}
			</div>
		</animated.div>
	);
};
